/**
 * Mastermind Spaces Service for Client-Side Operations
 * Handles all mastermind space CRUD operations using Supabase directly
 */

import { supabase, isSupabaseConfigured, getCurrentSession } from "@/lib/supabase";
import type { MastermindSpace } from "@shared/schema";
import { workspacesService } from "@/services/workspacesService";
import { spaceSharingService } from "@/services/spaceSharingService";
import { topicSharingService } from "@/services/topicSharingService";
import { threadSharingService } from "@/services/threadSharingService";
import { scheduleScopeEmbedding } from "@/services/scopeEmbeddingJobs";

export interface InsertMastermindSpace {
    name: string;
    description?: string | null;
    icon?: string | null;
    color?: string | null;
    goals?: string[];
    agents?: any;
    linkedSourceIds?: string[];
    milestones?: any;
    lens?: {
        purpose?: string;
        priorities?: string;
        details?: string;
    };
}

export interface TransferSpaceOptions {
    preserveShares?: boolean;
}

/** True if thread is tied to this space via linkedSpaceIds or topics in this space. */
function threadBelongsToSpaceTransfer(
    row: {
        linkedSpaceIds?: unknown;
        topicId?: unknown;
        topicIds?: unknown;
    },
    spaceId: string,
    topicIdsInSpace: string[]
): boolean {
    const linked = Array.isArray(row.linkedSpaceIds)
        ? row.linkedSpaceIds.filter((id: unknown) => id != null).map((id: unknown) => String(id))
        : [];
    if (linked.includes(spaceId)) return true;

    const singleTopic = row.topicId != null ? String(row.topicId) : null;
    if (singleTopic && topicIdsInSpace.includes(singleTopic)) return true;

    const many = Array.isArray(row.topicIds)
        ? row.topicIds.filter((id: unknown) => id != null).map((id: unknown) => String(id))
        : [];
    return many.some((id: string) => topicIdsInSpace.includes(id));
}

/** DEV: log each space’s logical origin (workspace query vs personal/shared bundle). */
function logMastermindSpacesOrigin(
    label: string,
    activeWorkspaceId: string | null | undefined,
    spaces: MastermindSpace[],
    workspaceScopedIds: Set<string>
): void {
    if (!import.meta.env.DEV) return;
    console.log("[getMastermindSpaces] space origin", {
        label,
        activeWorkspaceId: activeWorkspaceId ?? null,
        count: spaces.length,
        spaces: spaces.map((s) => ({
            id: s.id,
            name: s.name,
            workspaceIdOnRow: s.workspaceId ?? null,
            isShared: s.isShared === true,
            origin: workspaceScopedIds.has(s.id)
                ? "workspace"
                : s.isShared
                  ? "personal_shared"
                  : "personal_owned",
        })),
    });
}

/** Normalize linkedSpaceIds from a sources row (camelCase in your DB). */
function sourceLinkedSpaceIdsRow(source: Record<string, unknown>): string[] {
    const v = source.linkedSpaceIds;
    return Array.isArray(v) ? v.filter((id) => id != null).map((id) => String(id)) : [];
}

/** Columns that exist on public.sources (no linkedTopicIds in DB). Transfer only matches via linkedSpaceIds. */
const SOURCES_TRANSFER_SELECT = "id, userId, linkedSpaceIds";

class MastermindSpacesService {
    /**
     * Generate a simple unique ID
     */
    private generateSimpleUniqueId(): string {
        const timestamp = Date.now().toString(36);
        const randomPart = Math.random().toString(36).substring(2, 9);
        return timestamp + randomPart;
    }

    /**
     * Get current user ID from centralized session (single auth call on load)
     */
    private async getCurrentUserId(): Promise<string> {
        if (!isSupabaseConfigured) {
            throw new Error("Service not configured. Please try again later.");
        }
        const session = await getCurrentSession();
        if (!session?.user) {
            throw new Error("User not authenticated. Please log in.");
        }
        return session.user.id;
    }

    /**
     * Map database row (snake_case) to MastermindSpace (camelCase)
     * Matches the exact structure from server/services/supabase-mastermind.service.ts
     */
    private mapToMastermindSpace(
        item: any,
        isShared: boolean = false,
        opts?: { sharedMemberRole?: "editor" | "viewer"; currentUserId?: string }
    ): MastermindSpace {
        const uid = opts?.currentUserId;
        const isMastermindOwner =
            uid != null && item?.user_id != null && String(item.user_id) === String(uid);
        const row: Record<string, unknown> = {
            id: item.id,
            workspaceId: item.workspace_id ?? undefined,
            name: item.name,
            description: item.description,
            icon: item.icon,
            color: item.color,
            goals: item.goals || [],
            agents: item.agents || [],
            linkedSourceIds: item.linked_source_ids || [],
            milestones: item.milestones || [],
            lens: item.lens || { purpose: "", priorities: "", details: "" },
            notes: item.notes || null,
            isLocked: item.is_locked === true || item.is_locked === "true",
            lockedAt: item.locked_at ?? item.lockedAt,
            createdAt: item.created_at ?? item.createdAt,
            updatedAt: item.updated_at ?? item.updatedAt,
            isShared: isShared,
            includeInRag: item.include_in_rag !== false,
        };
        if (item.deleted_at != null) row.deletedAt = item.deleted_at;
        if (item.space_status != null) row.spaceStatus = item.space_status;
        if (item.merged_into_space_id != null) row.mergedIntoSpaceId = item.merged_into_space_id;
        if (uid != null) {
            row.isMastermindOwner = isMastermindOwner;
        }
        if (opts?.sharedMemberRole != null) {
            row.sharedMemberRole = opts.sharedMemberRole;
        }
        return row as MastermindSpace & {
            isShared?: boolean;
            includeInRag?: boolean;
            sharedMemberRole?: "editor" | "viewer";
            isMastermindOwner?: boolean;
        };
    }

    /**
     * Personal + shared-via-link spaces only (`workspace_id` IS NULL on mastermind).
     * Shared spaces always live on the owner's personal row.
     */
    private async getPersonalAndSharedMastermindSpaces(userId: string): Promise<MastermindSpace[]> {
        const { data: ownedSpaces, error: ownedError } = await supabase
            .from("mastermind")
            .select("*")
            .eq("user_id", userId)
            .is("workspace_id", null)
            .is("deleted_at", null)
            .order("updated_at", { ascending: false });

        if (ownedError) {
            console.error("Error fetching owned spaces:", ownedError);
            throw new Error(`Failed to fetch mastermind spaces: ${ownedError.message}`);
        }

        const { data: shareAccess } = await supabase
            .from("space_share_access")
            .select("share_id, status, role")
            .eq("user_id", userId)
            .eq("status", "accepted");

        const shareIds: string[] = (shareAccess || []).map((access: any) => access.share_id);
        const spaceIdToRole = new Map<string, "editor" | "viewer">();
        let sharedSpaces: any[] = [];
        if (shareIds.length > 0) {
            const { data: shares } = await supabase
                .from("space_shares")
                .select("id, space_id, is_active")
                .in("id", shareIds)
                .eq("is_active", "true");
            const shareIdToSpaceId = new Map<string, string>();
            for (const sh of shares || []) {
                shareIdToSpaceId.set(String((sh as any).id), String((sh as any).space_id));
            }
            for (const access of shareAccess || []) {
                const sid = shareIdToSpaceId.get(String((access as any).share_id));
                if (!sid) continue;
                const r = (access as any).role === "editor" ? "editor" : "viewer";
                const prev = spaceIdToRole.get(sid);
                if (!prev || prev === "viewer") {
                    spaceIdToRole.set(sid, r);
                }
            }
            const sharedSpaceIds = [...new Set((shares || []).map((share: any) => share.space_id))];
            if (sharedSpaceIds.length > 0) {
                const { data: sharedSpacesData } = await supabase
                    .from("mastermind")
                    .select("*")
                    .in("id", sharedSpaceIds)
                    .is("workspace_id", null)
                    .is("deleted_at", null)
                    .order("updated_at", { ascending: false });
                sharedSpaces = sharedSpacesData || [];
            }
        }

        const allSpaces = [
            ...(ownedSpaces || []).map((item: any) => this.mapToMastermindSpace(item, false, { currentUserId: userId })),
            ...sharedSpaces.map((item: any) =>
                this.mapToMastermindSpace(item, true, {
                    currentUserId: userId,
                    sharedMemberRole: spaceIdToRole.get(String(item.id)) ?? "viewer",
                })
            ),
        ];
        allSpaces.sort((a, b) => {
            const aVal = a.updatedAt ?? a.createdAt ?? 0;
            const bVal = b.updatedAt ?? b.createdAt ?? 0;
            return new Date(bVal).getTime() - new Date(aVal).getTime();
        });
        return allSpaces;
    }

    /**
     * Get all mastermind spaces for the current user.
     * @param workspaceId - When null/undefined: personal + shared-via-link spaces (`workspace_id` null).
     * When set: **only** spaces whose `workspace_id` matches (no personal or other-workspace rows).
     */
    async getMastermindSpaces(workspaceId?: string | null): Promise<MastermindSpace[]> {
        const userId = await this.getCurrentUserId();

        if (workspaceId != null && workspaceId !== "") {
            const hasAccess = await workspacesService.hasAccess(workspaceId);
            if (!hasAccess) {
                if (import.meta.env.DEV) {
                    console.debug("[getMastermindSpaces] no workspace access; returning empty", { workspaceId });
                }
                logMastermindSpacesOrigin("no_workspace_access", workspaceId, [], new Set());
                return [];
            }

            const { data: wsRows, error } = await supabase
                .from("mastermind")
                .select("*")
                .eq("workspace_id", workspaceId)
                .is("deleted_at", null)
                .order("updated_at", { ascending: false });

            if (error) throw new Error(`Failed to fetch workspace spaces: ${error.message}`);

            const workspaceMapped = (wsRows || []).map((item: any) =>
                this.mapToMastermindSpace(item, false, { currentUserId: userId })
            );

            if (import.meta.env.DEV) {
                console.debug("[getMastermindSpaces] workspace only", {
                    workspaceId,
                    count: workspaceMapped.length,
                });
            }

            const workspaceScopedIds = new Set(workspaceMapped.map((s) => s.id));
            logMastermindSpacesOrigin("workspace_only", workspaceId, workspaceMapped, workspaceScopedIds);

            return workspaceMapped;
        }

        const personalAndShared = await this.getPersonalAndSharedMastermindSpaces(userId);

        if (import.meta.env.DEV) {
            console.debug("[getMastermindSpaces] personal context (no workspace filter)", {
                personalAndSharedCount: personalAndShared.length,
                sharedCount: personalAndShared.filter((s) => s.isShared).length,
            });
        }

        logMastermindSpacesOrigin("personal_only", null, personalAndShared, new Set());
        return personalAndShared;
    }

    /**
     * Get a single mastermind space by ID.
     * @param id - Space id
     * @param workspaceId - When set, only a row with this `workspace_id` is returned (no personal/shared fallback).
     * When null, personal owned or shared-via-link.
     */
    async getMastermindSpace(id: string, workspaceId?: string | null): Promise<MastermindSpace | null> {
        const userId = await this.getCurrentUserId();

        if (workspaceId != null && workspaceId !== "") {
            const hasAccess = await workspacesService.hasAccess(workspaceId);
            if (!hasAccess) return null;
            const { data: wsSpace, error: wsErr } = await supabase
                .from("mastermind")
                .select("*")
                .eq("id", id)
                .eq("workspace_id", workspaceId)
                .is("deleted_at", null)
                .maybeSingle();
            if (!wsErr && wsSpace) {
                return this.mapToMastermindSpace(wsSpace, false, { currentUserId: userId });
            }
            return null;
        }

        const { data: ownedSpace, error: ownedError } = await supabase
            .from("mastermind")
            .select("*")
            .eq("id", id)
            .eq("user_id", userId)
            .is("workspace_id", null)
            .is("deleted_at", null)
            .maybeSingle();

        if (!ownedError && ownedSpace) return this.mapToMastermindSpace(ownedSpace, false, { currentUserId: userId });

        const { data: shares } = await supabase
            .from("space_shares")
            .select("id")
            .eq("space_id", id)
            .eq("is_active", "true");
        if (!shares?.length) return null;

        const { data: shareAccess } = await supabase
            .from("space_share_access")
            .select("id, share_id, status, role")
            .eq("user_id", userId)
            .eq("status", "accepted")
            .in("share_id", shares.map((s: any) => s.id))
            .maybeSingle();

        if (!shareAccess) return null;

        const { data: sharedSpace, error: spaceError } = await supabase
            .from("mastermind")
            .select("*")
            .eq("id", id)
            .is("deleted_at", null)
            .single();
        if (spaceError || !sharedSpace) return null;
        const sr = (shareAccess as any).role === "editor" ? "editor" : "viewer";
        return this.mapToMastermindSpace(sharedSpace, true, { currentUserId: userId, sharedMemberRole: sr });
    }

    /**
     * Create a new mastermind space.
     * @param spaceData - Space fields
     * @param workspaceId - When set, space is created in the workspace; when null/undefined, personal space.
     */
    async createMastermindSpace(spaceData: InsertMastermindSpace, workspaceId?: string | null): Promise<MastermindSpace> {
        const userId = await this.getCurrentUserId();

        if (!spaceData.name || !spaceData.name.trim()) {
            throw new Error("Name is required");
        }

        if (workspaceId != null && workspaceId !== "") {
            const hasAccess = await workspacesService.hasAccess(workspaceId);
            if (!hasAccess) throw new Error("You do not have access to this workspace");
        }

        const now = new Date().toISOString();

        const spaceToInsert: any = {
            id: this.generateSimpleUniqueId(),
            user_id: userId,
            name: spaceData.name.trim(),
            description: spaceData.description || null,
            icon: spaceData.icon || null,
            color: spaceData.color || null,
            goals: spaceData.goals || [],
            agents: spaceData.agents || [],
            linked_source_ids: spaceData.linkedSourceIds || [],
            milestones: spaceData.milestones || [],
            created_at: now,
            updated_at: now,
        };
        if (workspaceId != null && workspaceId !== "") {
            spaceToInsert.workspace_id = workspaceId;
        }

        // Only include lens if it's explicitly provided (column may not exist yet)
        // If lens column exists in DB, it will use the default value from the migration
        if (spaceData.lens !== undefined && spaceData.lens !== null) {
            spaceToInsert.lens = spaceData.lens;
        }

        console.log("Creating space with user_id:", userId);
        console.log("Space data to insert:", { ...spaceToInsert, goals: spaceToInsert.goals?.length, agents: spaceToInsert.agents?.length });

        const { data, error } = await supabase
            .from("mastermind")
            .insert(spaceToInsert)
            .select()
            .single();

        if (data) {
            console.log("Space created successfully. Returned user_id:", data.user_id);
        }

        if (error) {
            console.error("Error creating mastermind space:", error);
            throw new Error(`Failed to create mastermind space: ${error.message}`);
        }

        if (!data) {
            throw new Error("Failed to create mastermind space: No data returned");
        }

        scheduleScopeEmbedding("space", String(data.id));
        return this.mapToMastermindSpace(data, false, { currentUserId: userId });
    }

    /**
     * Update an existing mastermind space.
     * @param workspaceId - When set, space must belong to this workspace; when null, personal.
     */
    async updateMastermindSpace(
        id: string,
        updates: Partial<InsertMastermindSpace>,
        workspaceId?: string | null
    ): Promise<MastermindSpace | null> {
        const userId = await this.getCurrentUserId();
        const existing = await this.getMastermindSpace(id, workspaceId);
        if (!existing) return null;

        const updateData: any = { updated_at: new Date().toISOString() };
        if (updates.name !== undefined) {
            if (!updates.name || !updates.name.trim()) throw new Error("Name cannot be empty");
            updateData.name = updates.name.trim();
        }
        if (updates.description !== undefined) updateData.description = updates.description;
        if (updates.icon !== undefined) updateData.icon = updates.icon;
        if (updates.color !== undefined) updateData.color = updates.color;
        if (updates.goals !== undefined) updateData.goals = updates.goals;
        if (updates.agents !== undefined) updateData.agents = updates.agents;
        if (updates.linkedSourceIds !== undefined) updateData.linked_source_ids = updates.linkedSourceIds;
        if (updates.milestones !== undefined) updateData.milestones = updates.milestones;
        if (updates.lens !== undefined && updates.lens !== null) updateData.lens = updates.lens;
        if (updates.notes !== undefined) updateData.notes = updates.notes;
        if ((updates as { includeInRag?: boolean }).includeInRag !== undefined) {
            updateData.include_in_rag = (updates as { includeInRag?: boolean }).includeInRag;
        }

        let query = supabase.from("mastermind").update(updateData).eq("id", id);
        if (workspaceId != null && workspaceId !== "") {
            query = query.eq("workspace_id", workspaceId);
        } else {
            query = query.eq("user_id", userId).is("workspace_id", null);
        }
        const { data, error } = await query.select().single();

        if (error) {
            if (error.code === "PGRST116") return null;
            throw new Error(`Failed to update mastermind space: ${error.message}`);
        }
        if (
            data &&
            (updates.name !== undefined || updates.description !== undefined || updates.goals !== undefined)
        ) {
            scheduleScopeEmbedding("space", id);
        }
        return data ? this.mapToMastermindSpace(data, false, { currentUserId: userId }) : null;
    }

    /**
     * Delete a mastermind space.
     * @param workspaceId - When set, space must belong to this workspace; when null, personal.
     */
    async deleteMastermindSpace(id: string, workspaceId?: string | null): Promise<boolean> {
        const userId = await this.getCurrentUserId();
        const existing = await this.getMastermindSpace(id, workspaceId);
        if (!existing) return false;

        let query = supabase.from("mastermind").delete().eq("id", id);
        if (workspaceId != null && workspaceId !== "") {
            query = query.eq("workspace_id", workspaceId);
        } else {
            query = query.eq("user_id", userId).is("workspace_id", null);
        }
        const { error } = await query;

        if (error) {
            console.error("Error deleting mastermind space:", error);
            throw new Error(`Failed to delete mastermind space: ${error.message}`);
        }

        return true;
    }

    /**
     * Touch a space: update its updated_at so it appears as "most recent" in the sidebar.
     * Only updates owned spaces (user_id match). Safe to call when opening a space.
     */
    async touchSpace(id: string): Promise<void> {
        const userId = await this.getCurrentUserId();
        const { error } = await supabase
            .from("mastermind")
            .update({ updated_at: new Date().toISOString() })
            .eq("id", id)
            .eq("user_id", userId);

        if (error) {
            console.warn("Failed to touch space (may be shared or missing):", error.message);
        }
    }

    /**
     * Create a space from template with topics
     * Matches the server implementation at /api/spaces/from-template
     */
    async createFromTemplate(params: {
        name?: string;
        template: {
            name?: string;
            description?: string;
            icon?: string;
            color?: string;
            goals?: string[];
            topics?: Array<{
                name: string;
                description?: string;
                icon?: string;
            }>;
        };
    }): Promise<{
        space: MastermindSpace;
        topicsCreated: number;
        message: string;
    }> {
        const userId = await this.getCurrentUserId();
        
        // Create the space with user_id - the createMastermindSpace already handles this
        const { name, template } = params;

        if (!template) {
            throw new Error("Template is required");
        }

        // Create the space - matches server logic exactly
        const spaceData: InsertMastermindSpace = {
            name: name || template.name || "New Space",
            description: template.description || "",
            icon: template.icon || "Brain",
            color: template.color || "hsl(220, 70%, 50%)",
            goals: template.goals || [],
            agents: [],
            linkedSourceIds: [],
            milestones: [],
        };

        const space = await this.createMastermindSpace(spaceData);

        // Create topics from template
        let topicsCreated = 0;
        if (template.topics && Array.isArray(template.topics)) {
            for (const topicTemplate of template.topics) {
                try {
                    const { data: topic, error } = await supabase
                        .from("topics")
                        .insert({
                            user_id: userId,
                            space_id: space.id,
                            name: topicTemplate.name,
                            description: topicTemplate.description || "",
                            icon: topicTemplate.icon || "folder",
                        })
                        .select()
                        .single();

                    if (!error && topic) {
                        topicsCreated++;
                        scheduleScopeEmbedding("topic", String(topic.id));
                    }
                } catch (topicError) {
                    console.error("Error creating topic from template:", topicError);
                }
            }
        }

        // Log activity
        await supabase.from("activity_logs").insert({
            user_id: userId,
            action: "space_created_from_template",
            entity_type: "mastermind_space",
            entity_id: space.id,
            entity_title: space.name,
        });

        return {
            space,
            topicsCreated,
            message: `Created "${space.name}" with ${topicsCreated} topics`,
        };
    }

    /**
     * Lock or unlock a mastermind space.
     * @param workspaceId - When set, space must belong to this workspace; when null, personal.
     */
    async lockSpace(id: string, lock: boolean, workspaceId?: string | null): Promise<MastermindSpace | null> {
        const userId = await this.getCurrentUserId();
        const existing = await this.getMastermindSpace(id, workspaceId);
        if (!existing) return null;

        const updateData: Record<string, unknown> = {
            is_locked: lock,
            locked_at: lock ? new Date().toISOString() : null,
            updated_at: new Date().toISOString(),
        };

        let query = supabase.from("mastermind").update(updateData).eq("id", id).select();
        if (workspaceId != null && workspaceId !== "") {
            query = query.eq("workspace_id", workspaceId);
        } else {
            query = query.eq("user_id", userId).is("workspace_id", null);
        }
        const { data, error } = await query.single();

        if (error) {
            console.error("Error locking/unlocking space:", error);
            throw new Error(`Failed to update lock status: ${error.message}`);
        }

        if (!data) return null;
        return this.mapToMastermindSpace(data, false, { currentUserId: userId });
    }

    private async fetchSpaceMergeDebugRow(spaceId: string) {
        const { data, error } = await supabase
            .from("mastermind")
            .select("id, user_id, workspace_id, space_status, deleted_at, merged_into_space_id, name")
            .eq("id", spaceId)
            .maybeSingle();
        return { row: data, fetchError: error };
    }

    private logSupabaseRpcError(label: string, error: { message?: string; code?: string; details?: string; hint?: string; status?: number }) {
        console.warn(`[SpaceMergeDebug] ${label}`, {
            message: error.message,
            code: error.code,
            details: error.details,
            hint: error.hint,
            status: error.status,
            raw: error,
        });
    }

    /**
     * Merge one personal space into another (same owner). Runs atomically in DB (merge_space_into):
     * moves topics, threads, knowledge docs, sources; verifies; soft-deletes the source for 72h.
     */
    async mergeSpaceInto(sourceSpaceId: string, targetSpaceId: string): Promise<{
        topicsMoved: number;
        threadsMoved: number;
        documentsMoved: number;
        sourcesMoved: number;
        message: string;
        softDeletedAt?: string;
        purgeAfter?: string;
    }> {
        const session = await getCurrentSession();
        const authUserId = session?.user?.id ?? null;

        console.group("[SpaceMergeDebug] mergeSpaceInto");
        console.log("request", {
            rpc: "merge_space_into",
            p_source_space_id: sourceSpaceId,
            p_target_space_id: targetSpaceId,
        });
        console.log("authUserId", authUserId);

        const [sourceDebug, targetDebug, sourceRole, targetRole] = await Promise.all([
            this.fetchSpaceMergeDebugRow(sourceSpaceId),
            this.fetchSpaceMergeDebugRow(targetSpaceId),
            spaceSharingService.getCurrentUserSpaceRole(sourceSpaceId).catch((e) => ({ error: String(e) })),
            spaceSharingService.getCurrentUserSpaceRole(targetSpaceId).catch((e) => ({ error: String(e) })),
        ]);

        console.log("sourceRow", sourceDebug.row, "sourceRowFetchError", sourceDebug.fetchError);
        console.log("targetRow", targetDebug.row, "targetRowFetchError", targetDebug.fetchError);
        console.log("sourceSpaceRole", sourceRole);
        console.log("targetSpaceRole", targetRole);

        const { data, error } = await supabase.rpc("merge_space_into", {
            p_source_space_id: sourceSpaceId,
            p_target_space_id: targetSpaceId,
        });

        console.log("response", { data, error: error ?? null });
        if (error) {
            this.logSupabaseRpcError("mergeSpaceInto RPC error", error);
            console.groupEnd();
            throw new Error(error.message || "Merge failed");
        }
        console.groupEnd();

        const row = data as {
            ok?: boolean;
            topicsMoved?: number;
            threadsMoved?: number;
            documentsMoved?: number;
            sourcesMoved?: number;
            softDeletedAt?: string;
            purgeAfter?: string;
            sourceName?: string;
            targetName?: string;
        } | null;

        if (!row || row.ok !== true) {
            throw new Error("Merge failed");
        }

        const topicsMoved = row.topicsMoved ?? 0;
        const threadsMoved = row.threadsMoved ?? 0;
        const documentsMoved = row.documentsMoved ?? 0;
        const sourcesMoved = row.sourcesMoved ?? 0;
        const srcName = row.sourceName ?? "Source";
        const tgtName = row.targetName ?? "Target";

        return {
            topicsMoved,
            threadsMoved,
            documentsMoved,
            sourcesMoved,
            softDeletedAt: row.softDeletedAt,
            purgeAfter: row.purgeAfter,
            message: `Merged "${srcName}" into "${tgtName}" (${topicsMoved} topics, ${threadsMoved} threads, ${documentsMoved} documents, ${sourcesMoved} sources). The source space is archived for 72 hours; you can restore it from Mastermind Spaces if needed.`,
        };
    }

    /**
     * Personal spaces merged into another and awaiting purge (soft-deleted, restorable within 72h).
     */
    async listPendingMergeArchiveSpaces(): Promise<MastermindSpace[]> {
        const userId = await this.getCurrentUserId();
        const { data, error } = await supabase.rpc("list_pending_merge_archive_spaces");

        if (error) {
            throw new Error(`Failed to list archived merges: ${error.message}`);
        }

        const rows = (Array.isArray(data) ? data : []) as Record<string, unknown>[];
        return rows.map((item) => this.mapToMastermindSpace(item, false, { currentUserId: userId }));
    }

    /**
     * Undo a merge: moves content back to the source space and clears soft-delete (within 72h of merge).
     */
    async restoreMergedSpace(sourceSpaceId: string): Promise<{
        message: string;
        restoredSpaceId: string;
        destinationSpaceId: string | null;
    }> {
        const session = await getCurrentSession();
        const authUserId = session?.user?.id ?? null;

        console.group("[SpaceMergeDebug] restoreMergedSpace");
        console.log("request", {
            rpc: "restore_merged_space",
            p_source_space_id: sourceSpaceId,
        });
        console.log("authUserId", authUserId);

        const [sourceDebug, sourceRole] = await Promise.all([
            this.fetchSpaceMergeDebugRow(sourceSpaceId),
            spaceSharingService.getCurrentUserSpaceRole(sourceSpaceId).catch((e) => ({ error: String(e) })),
        ]);
        console.log("sourceRow", sourceDebug.row, "sourceRowFetchError", sourceDebug.fetchError);
        console.log("sourceSpaceRole", sourceRole);

        const { data, error } = await supabase.rpc("restore_merged_space", {
            p_source_space_id: sourceSpaceId,
        });

        console.log("response", { data, error: error ?? null });
        if (error) {
            this.logSupabaseRpcError("restoreMergedSpace RPC error", error);
            console.groupEnd();
            throw new Error(error.message || "Restore failed");
        }
        console.groupEnd();
        const row = data as {
            ok?: boolean;
            restoredSpaceId?: string;
            destinationSpaceId?: string;
        } | null;
        const restoredSpaceId = row?.restoredSpaceId ?? sourceSpaceId;
        const destinationSpaceId =
            row?.destinationSpaceId != null && row.destinationSpaceId !== ""
                ? String(row.destinationSpaceId)
                : null;
        return {
            message: "Merge undone. Your spaces have been restored.",
            restoredSpaceId,
            destinationSpaceId,
        };
    }

    async transferSpaceToWorkspace(
        spaceId: string,
        targetWorkspaceId: string | null,
        options: TransferSpaceOptions = {}
    ): Promise<{
        topicsMoved: number;
        threadsMoved: number;
        knowledgeDocsMoved: number;
        sourceDocsMoved: number;
        linksUpdated: number;
        sharesRevoked: number;
        message: string;
    }> {
        const userId = await this.getCurrentUserId();
        const now = new Date().toISOString();
        const preserveShares = options.preserveShares !== false;

        const { data: sourceSpace, error: sourceError } = await supabase
            .from("mastermind")
            .select("id, name, user_id, workspace_id")
            .eq("id", spaceId)
            .eq("user_id", userId)
            .maybeSingle();

        if (sourceError || !sourceSpace) {
            throw new Error("Space not found or you don't have permission to transfer it");
        }

        const normalizedSourceWorkspace = (sourceSpace as any).workspace_id ?? null;
        const normalizedTargetWorkspace = targetWorkspaceId && targetWorkspaceId !== "" ? targetWorkspaceId : null;

        if (normalizedSourceWorkspace === normalizedTargetWorkspace) {
            throw new Error("Space is already in that workspace");
        }

        if (normalizedTargetWorkspace) {
            const hasAccess = await workspacesService.hasAccess(normalizedTargetWorkspace);
            if (!hasAccess) {
                throw new Error("You do not have access to the destination workspace");
            }
        }

        const { data: movedTopics, error: movedTopicsError } = await supabase
            .from("topics")
            .select("id")
            .eq("space_id", spaceId);
        if (movedTopicsError) {
            throw new Error(`Failed to load related topics: ${movedTopicsError.message}`);
        }
        const movedTopicIds = (movedTopics || []).map((t: any) => String(t.id));

        // JSONB linkedSpaceIds: do not use .contains() (400 / invalid json on Supabase)
        const { data: threadRows, error: threadLoadError } = await supabase
            .from("chat-threads")
            .select("id, linkedSpaceIds, topicId, topicIds")
            .eq("userId", userId)
            .order("updatedAt", { ascending: false })
            .limit(2000);
        if (threadLoadError) {
            throw new Error(`Failed to load related threads: ${threadLoadError.message}`);
        }
        const movedThreadIds = [
            ...new Set(
                (threadRows || [])
                    .filter((t) => threadBelongsToSpaceTransfer(t, spaceId, movedTopicIds))
                    .map((t: { id: string }) => String(t.id))
            ),
        ];

        const spaceUpdate: Record<string, unknown> = {
            workspace_id: normalizedTargetWorkspace,
            updated_at: now,
        };
        const { error: updateSpaceError } = await supabase
            .from("mastermind")
            .update(spaceUpdate)
            .eq("id", spaceId)
            .eq("user_id", userId);
        if (updateSpaceError) {
            throw new Error(`Failed to update space workspace: ${updateSpaceError.message}`);
        }

        let topicsMoved = 0;
        if (movedTopicIds.length > 0) {
            const topicPayload: Record<string, unknown> = {
                updated_at: now,
                workspaceId: normalizedTargetWorkspace,
            };
            const { data: topicsWsUpdate, error: topicsUpdateError } = await supabase
                .from("topics")
                .update(topicPayload)
                .eq("space_id", spaceId)
                .select("id");
            if (topicsUpdateError) {
                throw new Error(`Failed to move topics: ${topicsUpdateError.message}`);
            }
            topicsMoved = topicsWsUpdate?.length ?? 0;
        }

        let threadsMoved = 0;
        if (movedThreadIds.length > 0) {
            const { data: threadUpdates, error: threadUpdateError } = await supabase
                .from("chat-threads")
                .update({
                    workspace_id: normalizedTargetWorkspace,
                    updatedAt: now,
                })
                .in("id", movedThreadIds)
                .eq("userId", userId)
                .select("id");
            if (threadUpdateError) {
                throw new Error(`Failed to move threads: ${threadUpdateError.message}`);
            }
            threadsMoved = threadUpdates?.length ?? 0;
        }

        const { data: allKnowledgeDocs, error: knowledgeLoadError } = await supabase
            .from("knowledgebase")
            .select("id, metadata, spaceId, workspace_id")
            .eq("userId", userId);
        if (knowledgeLoadError) {
            throw new Error(`Failed to load knowledge docs: ${knowledgeLoadError.message}`);
        }

        const shouldMoveKnowledgeDoc = (doc: any): boolean => {
            if (doc.spaceId === spaceId) return true;
            let meta: any = doc.metadata || {};
            if (typeof meta === "string") {
                try {
                    meta = JSON.parse(meta);
                } catch {
                    meta = {};
                }
            }
            const docSpaceIds = Array.isArray(meta.spaceIds) ? meta.spaceIds.map((id: unknown) => String(id)) : [];
            if (docSpaceIds.includes(spaceId)) return true;

            const docTopicId = meta.topicId != null ? String(meta.topicId) : null;
            if (docTopicId && movedTopicIds.includes(docTopicId)) return true;
            const docTopicIds = Array.isArray(meta.topicIds) ? meta.topicIds.map((id: unknown) => String(id)) : [];
            return docTopicIds.some((id: string) => movedTopicIds.includes(id));
        };

        const knowledgeDocIdsToMove = (allKnowledgeDocs || [])
            .filter((doc: any) => shouldMoveKnowledgeDoc(doc))
            .map((doc: any) => String(doc.id));

        let knowledgeDocsMoved = 0;
        if (knowledgeDocIdsToMove.length > 0) {
            const { data: movedKnowledgeDocs, error: knowledgeUpdateError } = await supabase
                .from("knowledgebase")
                .update({ workspace_id: normalizedTargetWorkspace, updatedAt: now })
                .in("id", knowledgeDocIdsToMove)
                .eq("userId", userId)
                .select("id");
            if (knowledgeUpdateError) {
                throw new Error(`Failed to move knowledge docs: ${knowledgeUpdateError.message}`);
            }
            knowledgeDocsMoved = movedKnowledgeDocs?.length ?? 0;
        }

        let sourceDocsMoved = 0;
        const { data: allSources, error: sourcesLoadError } = await supabase
            .from("sources")
            .select(SOURCES_TRANSFER_SELECT)
            .eq("userId", userId);
        if (sourcesLoadError) {
            throw new Error(`Failed to load sources: ${sourcesLoadError.message}`);
        }
        // DB has no linkedTopicIds; topic-only-linked sources without this space in linkedSpaceIds are skipped.
        const sourceIdsToMove = (allSources || [])
            .filter((source: Record<string, unknown>) =>
                sourceLinkedSpaceIdsRow(source).includes(spaceId)
            )
            .map((source: Record<string, unknown>) => String(source.id));
        if (sourceIdsToMove.length > 0) {
            const { data: movedSources, error: sourceUpdateError } = await supabase
                .from("sources")
                .update({ workspace_id: normalizedTargetWorkspace, updatedAt: now })
                .in("id", sourceIdsToMove)
                .eq("userId", userId)
                .select("id");
            if (sourceUpdateError) {
                throw new Error(`Failed to move sources: ${sourceUpdateError.message}`);
            }
            sourceDocsMoved = movedSources?.length ?? 0;
        }

        // Documents are moved via knowledgebase + sources above; document_entity_links is not used for transfer.
        const linksUpdated = 0;

        let sharesRevoked = 0;
        if (!preserveShares) {
            try {
                if (await spaceSharingService.getShareLink(spaceId)) {
                    await spaceSharingService.revokeShareLink(spaceId);
                    sharesRevoked += 1;
                }
            } catch {
                // best effort
            }

            for (const topicId of movedTopicIds) {
                try {
                    if (await topicSharingService.getShareLink(topicId)) {
                        await topicSharingService.revokeShareLink(topicId);
                        sharesRevoked += 1;
                    }
                } catch {
                    // best effort
                }
            }

            for (const threadId of movedThreadIds) {
                try {
                    if (await threadSharingService.getShareLink(threadId)) {
                        await threadSharingService.revokeShareLink(threadId);
                        sharesRevoked += 1;
                    }
                } catch {
                    // best effort
                }
            }
        }

        const destinationLabel = normalizedTargetWorkspace ? "workspace" : "personal space";
        return {
            topicsMoved,
            threadsMoved,
            knowledgeDocsMoved,
            sourceDocsMoved,
            linksUpdated,
            sharesRevoked,
            message: `Moved "${sourceSpace.name}" to ${destinationLabel}.`,
        };
    }
}

/**
 * Create and export a singleton instance
 */
export const mastermindSpacesService = new MastermindSpacesService();
